import { Cookies } from 'react-cookie';

const periodRegex = /(\d*)H((\d*)M)?((\d*)S)?/;

export default {
  generateUUID: () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      /* eslint no-bitwise: ["error", { "allow": ["|", "&"] }] */
      const r = (Math.random() * 16) | 0;

      /* eslint no-mixed-operators: 0 */
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  },

  saveLocalStorage: (key, value) => {
    return localStorage.setItem(key, value);
  },

  getLocalStorage: (key) => {
    return localStorage.getItem(key) || '';
  },

  removeLocalStorage: (key) => {
    return localStorage.removeItem(key);
  },

  setCookie: (key, value) => {
    return new Cookies().set(key, value, { path: '/', sameSite: 'strict' });
  },

  getCookie: (key) => {
    return new Cookies().get(key, { path: '/' });
  },

  removeCookie: (key) => {
    return new Cookies().remove(key, { path: '/' });
  },

  getURLParameterByName: (name, url) => {
    let curl = url;
    let cname = name;
    if (!curl) {
      curl = window.location.href;
    }
    cname = cname.replace(/[[\]]/g, '\\$&');
    const regex = new RegExp(`[?&]${cname}(=([^&#]*)|&|#|$)`);
    const results = regex.exec(curl);

    if (!results) return null;
    if (!results[2]) return '';

    return decodeURIComponent(results[2].replace(/\+/g, ''));
  },

  isValidEmail: (email) => {
    const regex = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
    return regex.test(email);
  },

  // Check multiple emails, separated by commas
  isValidMultipleEmails: (emails) => {
    const regex = /^((\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*)\s*[,]{0,1}\s*)+$/i;
    return regex.test(emails);
  },

  isValidPhone: (phone) => {
    return /^\+1{1}[0-9]{10,}$/.test(phone);
  },

  isValidFormattedPhone: (phone) => {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    // Check if it starts with '1' and has 11 digits (US standard)
    return /^1[0-9]{10}$/.test(cleaned);
  },

  isValidURL: (url) => {
    return /^(?:(?:(?:https?|ftp):)?\/\/)?(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
      url,
    );
  },

  hyphenatePhone: (phone) => {
    if (!phone) return '';

    return phone.replace(/(\d*)(\d{3})(\d{3})(\d{4})/, '$1-$2-$3-$4');
  },

  isNumber: (value) => {
    return /\d+/.test(value);
  },
  isPositiveInteger: (value) => {
    return /^\+?(0|[1-9]\d*)$/.test(value);
  },
  isAudioRecordingSupported: () => {
    return !!(
      window.AudioContext &&
      global.navigator &&
      global.navigator.mediaDevices &&
      global.navigator.mediaDevices.getUserMedia &&
      global.WebAssembly
    );
  },

  getMimetype: (signature) => {
    switch (signature) {
      case '89504E47':
        return 'image/png';
      case '47494638':
        return 'image/gif';
      case '25504446':
        return 'application/pdf';
      case 'FFD8FFDB':
      case 'FFD8FFE0':
      case 'FFD8FFE1':
        return 'image/jpeg';
      case '504B0304':
        return 'application/zip';
      default:
        if (signature.startsWith('494433')) return 'audio/mp3';
        return 'Unknown filetype';
    }
  },

  parsePeriod: (duration) => {
    const res = (duration || '').match(periodRegex);
    return res ? [res[1], res[3], res[5]] : [];
  },
  formatPhoneNumber: (phoneNumberString) => {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      const intlCode = match[1] ? '+1 ' : '';
      return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
    } else {
      return phoneNumberString;
    }
  },
  isEmptyObject: (obj) => {
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) return false;
    }
    return true;
  },

  isLocationValid: (obj) => {
    if (obj === 'Multiple Location' || obj === 'Single Location') {
      return true;
    }
    return false;
  },
  arrayEquals: (a, b) => {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((val, index) => val === b[index])
    );
  },
  sanitizeHTML: (text) => {
    const element = document.createElement('div');
    element.innerText = text;
    return element.innerHTML;
  },
  arraysHaveSameValues: (arr1, arr2) => {
    // Convert arrays to sets
    const set1 = new Set(arr1);
    const set2 = new Set(arr2);

    // Check if sizes of sets are the same
    if (set1.size !== set2.size) {
      return false;
    }

    // Check if every element in set1 is in set2
    for (const item of set1) {
      if (!set2.has(item)) {
        return false;
      }
    }

    // If all checks pass, arrays have the same values
    return true;
  },
  ensureArray: (value) => {
    // Check if the value is an array
    if (Array.isArray(value)) {
      return value;
    } else if (typeof value === 'object' && value !== null) {
      return [value]; // Wrap the object inside an array
    } else {
      return null; // or handle it as needed
    }
  },
};
